// src/pages/Login.jsx
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import api from "../../api/api";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import GoogleButton from "react-google-button"; // ✅ Import Google button
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGoogleLogin = () => {
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post("/auth/login", formData);
      const { user } = response.data;
      login(user);
      navigate("/");
    } catch (error) {
      console.error("Login error:", error);
      alert(error.response?.data?.message || t("auth.login.error"));
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          {t("auth.login.title")}
        </h2>
        {/* ✅ Google Login Button */}
        <div className="mb-4 flex justify-center">
          <GoogleButton
            onClick={handleGoogleLogin}
            label={t("auth.login.googleButton")}
          />
        </div>

        {/* ✅ Separator */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500">
              {t("auth.common.orContinueWith")}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email Field */}
          <div className="mb-4">
            <label
              htmlFor="email"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              {t("auth.common.email")}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Password Field */}
          <div className="mb-6">
            <label
              htmlFor="password"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              {t("auth.common.password")}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:shadow-outline"
          >
            {t("auth.login.submit")}
          </button>
        </form>

        {/* Forgot password link */}
        <div className="mt-4 text-center space-y-2">
          <Link
            to="/forgot-password"
            className="text-sm text-blue-500 hover:text-blue-700 hover:underline"
          >
            {t("auth.login.forgotPassword")}
          </Link>

          <p className="text-sm text-gray-600">
            {t("auth.login.noAccount", "Don't have an account?")}{" "}
            <Link
              to="/signup"
              className="text-blue-500 hover:text-blue-700 hover:underline font-medium"
            >
              {t("auth.login.signUpNow", "Sign up now")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
